import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Phase 4g.J (Feb 2026, User-Wunsch) — Auto-Select voreingestellter Werte
 *
 * Wenn ein Input mit einem **nicht-leeren Voreinstellungs-Wert** befüllt ist,
 * werden beim ERSTEN Fokus alle Zeichen automatisch markiert. So genügt
 * eine einzige Taste, um den vorgefüllten Wert zu ersetzen → reduziert
 * Tippfehler (z.B. „0" + „5" → „05" statt „5").
 *
 * Nach der ersten Eingabe oder einem Blur greift das Verhalten nicht mehr
 * — der Cursor lässt sich dann ganz normal positionieren.
 *
 * Opt-out: ``<Input disableAutoSelect />``
 */
const Input = React.forwardRef(({ className, type, disableAutoSelect, onFocus, onChange, ...props }, ref) => {
  const interactedRef = React.useRef(false);
  const timerRef = React.useRef(null);

  // Ausstehenden Select-Timer beim Unmount aufräumen (verhindert Race nach Re-Render).
  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleFocus = React.useCallback((e) => {
    if (!disableAutoSelect && !interactedRef.current) {
      const el = e.target;
      const focusValue = el.value;
      // Nur auto-markieren, wenn ein vorbefüllter Wert vorhanden ist.
      if (focusValue !== "" && focusValue !== null && focusValue !== undefined) {
        if (timerRef.current) clearTimeout(timerRef.current);
        // Verzögert, damit der Browser-Fokus sitzt — ABER mit strengen Guards,
        // damit kein bereits getippter Wert nachträglich markiert wird (Freeze-Bug).
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          if (!interactedRef.current && document.activeElement === el && el.value === focusValue) {
            try { el.select(); } catch { /* element unmounted */ }
          }
        }, 0);
      }
    }
    if (typeof onFocus === "function") onFocus(e);
  }, [disableAutoSelect, onFocus]);

  const handleChange = React.useCallback((e) => {
    interactedRef.current = true;
    // Sobald getippt wird: ausstehenden Auto-Select abbrechen (kein Re-Select mehr).
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (typeof onChange === "function") onChange(e);
  }, [onChange]);

  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      onFocus={handleFocus}
      onChange={handleChange}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
